import { matchesWords, searchWordsOf } from './list_search.js';
import { queueWords } from './plain.js';
import { notACarrierName } from './side.js';
import { callResult } from './status.js';
import { dolLabel } from './time.js';
import { TODO_GROUPS } from './todo.js';
/** The test-case name rule (only where the Salesforce box is not ticked): "test" or "tester" as a whole word, any case. */
export const TEST_NAME = /\btest(er)?\b/i;
export function testCaseOf(flag, names) {
    if (flag === true)
        return 'flag';
    return names.some((n) => TEST_NAME.test(String(n ?? ''))) ? 'name' : null;
}
/** The age pill's words (Rod's coordinator 2026-10-13: "1966 d" is hard to read): days up to 99, then months, then years from 2 years
 *  (one decimal). The exact day count goes in the hover. */
export function ageWords(days) {
    if (days < 100)
        return `${days} d`;
    if (days < 730)
        return `${Math.round(days / 30.44)} mo`;
    return `${(days / 365.25).toFixed(1)} y`;
}
/** The case statuses All unopened reads (the server query and the Status filter). Cases in litigation are in (Neila 2026-10-13 9:49 pm: A,
 *  included under their own filter); leaving them out would be deleting 'Litigation' from this one line. */
export const UNOPENED_STATUSES = ['Open', 'Litigation'];
export const carrierKind = (c) => (!String(c ?? '').trim() ? 'none' : notACarrierName(c) ? 'placeholder' : 'real');
export const AGE_BUCKETS = [['d14', '14 days or less'], ['d90', '15 to 90 days'], ['y1', '91 days to a year'], ['old', 'Over a year']];
const ms = (s) => Date.parse(String(s).replace(/\+0000$/, 'Z'));
export const caseAgeDays = (openedAt, now) => Math.max(0, Math.floor((now.getTime() - ms(openedAt)) / 86_400_000));
export function caseAgeBucket(openedAt, now) {
    const d = caseAgeDays(openedAt, now);
    return d <= 14 ? 'd14' : d <= 90 ? 'd90' : d <= 365 ? 'y1' : 'old';
}
/** The controller's write scope (lib/claims_controller/item.js matterInScope + constants ACTIVE_STAGES), mirrored: outside it a typed fact
 *  would come back OUT_OF_SCOPE, so the row offers "Open in Salesforce" instead of an input. */
export const ACTIVE_STAGES = ['Treating', 'Records', 'Negotiation', 'Initial Investigations', 'Pursuing UIM'];
export const inControllerScope = (status, stage) => status === 'Open' && ACTIVE_STAGES.includes(String(stage ?? ''));
/** The record's light reason: the first thing that blocks it, in the to-do group it would join on the 14-day board. */
export function lightReason(r) {
    const k = carrierKind(r.carrier);
    if (k === 'none')
        return r.side === 'DEF' ? { group: 'insurer', fix: 'nocarrier', need: "Find the other driver's insurer and put it on the record" }
            : { group: 'client', fix: 'nocarrier', need: "Put the client's insurer on the record" };
    if (k === 'placeholder')
        return { group: 'carrier', fix: 'nocarrier', need: `The record's carrier is "${r.carrier}", not an insurer: put the real insurer on the record` };
    if (!r.policy)
        return { group: 'policy', fix: 'nopolicy', need: `Enter the policy number for ${r.carrier}${r.calls ? '' : ' (the AI cannot call without it)'}` };
    if (r.lastCall)
        return { group: 'call', fix: 'review', need: `Last AI call ${r.lastCall.result}: call or file with ${r.carrier}, then enter the claim number` };
    return { group: 'call', fix: 'nocall', need: `No AI call yet: call or file with ${r.carrier}, then enter the claim number` };
}
/** the short facts beside the reason (the cheap reads, in words) */
export function lightFacts(r) {
    return [r.policy ? 'policy on file' : 'no policy #', r.calls ? `${r.calls} AI call${r.calls === 1 ? '' : 's'}` : 'no AI call', r.lor === 'SENT' ? 'LOR sent' : r.lor ? `LOR: ${r.lor.toLowerCase()}` : 'no LOR',
        r.police ? 'police report on file' : 'no police report', r.asked ? 'the system asks a person' : null].filter((x) => !!x);
}
/** the last AI call on a record, in words (cheap: the call rows only) */
export function lastCallOf(calls) {
    const last = calls.slice().sort((a, b) => ms(b.CreatedDate) - ms(a.CreatedDate))[0];
    return last ? { at: last.CreatedDate, result: callResult(last) } : null;
}
/** A light record as a Queue row (the same row markup and actions as the 14-day board); its full Need is read when it is opened. */
export function unopenedItem(r) {
    const why = lightReason(r);
    const matter = { id: r.matterId, name: r.matterName, client: r.client, caseType: r.caseType, state: r.state, convertedAt: r.convertedAt,
        dol: r.dol, owner: null, caseManager: null, lead: r.lead, companionId: r.companionId, hi: null, hidden: 0, opened: 0, possible: 1, worst: 'need', noInsurance: false, gaps: [] };
    const row = { insuranceId: r.insuranceId, side: r.otherParty ? 'Other Party' : r.side, coverage: null, carrier: carrierKind(r.carrier) === 'real' ? r.carrier : r.carrier, policy: r.policy, status: 'need',
        text: why.need, fix: why.fix, claim: null, openedBy: null, openedByName: null, openedAt: null, nextAt: null, waitingSince: r.openedAt, lor: r.lor === 'SENT' ? 'LOR sent' : null,
        calls: r.calls, lastCall: r.lastCall ? { ...r.lastCall, note: null } : null, need: why.need, who: 'team' };
    return { matter, row, group: why.group, why: lightFacts(r).join(' · '), urgent: null, light: true, writable: r.writable };
}
/** The rows on one page of All unopened, the one place it is set (the footer's "100 per page" reads it back from the page). It stays 100
 *  when the medrec console's lists default to 50 (10-18): that default is its Settings page's, which Sweet Claims has none of, and 50 would
 *  double the presses through a list of about 3,870. */
export const UNOPENED_PAGE = 100;
const stateCodeOf = (s) => (s ? s.trim().split(/\s+/)[0].toUpperCase() : 'none');
/** carrier filter values: 'none', 'placeholder', or a carrier's name */
const carrierKey = (r) => { const k = carrierKind(r.carrier); return k === 'real' ? r.carrier : k; };
function keep(r, q, now, skip) {
    if (!q.second && !(r.lead || !r.companionId))
        return false;
    if (!q.leftovers && r.leftover)
        return false;
    if (!q.tests && r.test)
        return false;
    if (skip !== 'age' && q.age && caseAgeBucket(r.openedAt, now) !== q.age)
        return false;
    if (skip !== 'side' && q.side && r.side !== q.side)
        return false;
    if (skip !== 'state' && q.state && stateCodeOf(r.state) !== q.state.toUpperCase())
        return false;
    if (skip !== 'carrier' && q.carrier && carrierKey(r) !== q.carrier)
        return false;
    if (skip !== 'status' && q.status && r.status !== q.status)
        return false;
    if (skip !== 'asked' && q.asked && !r.asked)
        return false;
    if (q.q && !matchesWords(unopenedWords(r), searchWordsOf(q.q)))
        return false;
    return true;
}
/** A row's words for the top bar's search: what its cells show (the client, the matter, the state, the DOL, the case type, the insurer, the
 *  policy, the side, what to do, the action's words, the case's status and a DO NOT USE name). */
export function unopenedWords(r) {
    return [r.client, r.matterName, r.state, stateCodeOf(r.state), dolLabel(r.dol), r.caseType, carrierKind(r.carrier) === 'real' ? r.carrier : null, r.policy,
        r.side, r.otherParty ? 'OP' : null, queueWords(lightReason(r).need), UNOPENED_ACTION_WORDS[unopenedAction(r) ?? 'none'], r.status, r.doNotUse ?? null];
}
/** oldest case first, then the record */
const byOldest = (a, b) => ms(a.openedAt) - ms(b.openedAt) || a.insuranceId.localeCompare(b.insuranceId);
/** Each filter's counts with every OTHER filter applied (so a count always says what pressing it would show). */
export function unopenedFacets(recs, q, now) {
    const age = { d14: 0, d90: 0, y1: 0, old: 0 };
    const side = { PLT: 0, DEF: 0 };
    const state = new Map();
    const status = new Map();
    const car = new Map();
    let none = 0, placeholder = 0, asked = 0, second = 0, leftovers = 0;
    const tests = { flag: 0, name: 0 };
    for (const r of recs) {
        if (keep(r, q, now, 'age'))
            age[caseAgeBucket(r.openedAt, now)]++;
        if (keep(r, q, now, 'side'))
            side[r.side]++;
        if (keep(r, q, now, 'state'))
            state.set(stateCodeOf(r.state), (state.get(stateCodeOf(r.state)) ?? 0) + 1);
        if (keep(r, q, now, 'status'))
            status.set(r.status, (status.get(r.status) ?? 0) + 1);
        if (keep(r, q, now, 'carrier')) {
            const k = carrierKey(r);
            if (k === 'none')
                none++;
            else if (k === 'placeholder')
                placeholder++;
            else
                car.set(k, (car.get(k) ?? 0) + 1);
        }
        if (keep(r, q, now, 'asked') && r.asked)
            asked++;
        if (!(r.lead || !r.companionId) && keep(r, { ...q, second: true }, now))
            second++;
        if (r.test && keep(r, { ...q, tests: true }, now))
            tests[r.test]++;
        if (r.leftover && keep(r, { ...q, leftovers: true }, now))
            leftovers++;
    }
    const sorted = (m) => [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return { age, side, state: sorted(state), status: sorted(status), carrier: { none, placeholder, top: sorted(car).slice(0, 15) }, asked, second, leftovers, tests };
}
// THE SERVER'S SORT (the Ins Creation chat, 10-18: a sort that ordered only the 100 rows on screen read as "the oldest unopened claims"). The
// columns are the Queue's own header keys (web queue-sort.ts QUEUE_SORT), so one ?sort=&dir= pair in the address means the same column on
// every tab; each value is what the row shows in that column. Text compares as the web lists do (list/list-sort.ts: case and accents aside,
// numbers as numbers); an empty value sits after every value in both directions; ties break on the case's age, then the record id, so the
// order is total and the pages never overlap or skip.
export const UNOPENED_SORT_KEYS = ['since', 'case', 'record', 'need', 'action', 'links'];
/** The sort an address asks for: a known column, ascending unless `desc`; anything else (absent, unknown) = null, oldest case first. */
export function unopenedSortOf(key, dir) {
    const k = UNOPENED_SORT_KEYS.find((x) => x === key);
    return k ? { key: k, dir: dir === 'desc' ? 'desc' : 'asc' } : null;
}
/** The press a row offers in its Action column (web queue.tsx UnopenedRow): a policy or claim number typed in place where the controller
 *  writes, else none ("Open in Salesforce"). */
export function unopenedAction(r) {
    if (!r.writable)
        return null;
    const g = lightReason(r).group;
    return g === 'policy' ? 'policy_number' : g === 'call' ? 'claim_number' : null;
}
/** The Action column's words, for its sort (the same words as web facts.tsx FACT_BUTTON; a web test keeps them equal). */
export const UNOPENED_ACTION_WORDS = Object.freeze({ policy_number: 'Enter policy #', claim_number: 'Enter claim #', none: 'Open in Salesforce' });
const COLLATOR = new Intl.Collator('en-US', { sensitivity: 'base', numeric: true });
function unopenedSortValue(r, key) {
    switch (key) {
        case 'since': {
            const t = ms(r.openedAt);
            return Number.isFinite(t) ? t : null;
        }
        case 'case': return r.client || null;
        case 'record': return carrierKind(r.carrier) === 'real' ? r.carrier : null;
        case 'need': return queueWords(lightReason(r).need) || null;
        case 'action': return UNOPENED_ACTION_WORDS[unopenedAction(r) ?? 'none'];
        case 'links': return r.matterName || null;
    }
}
function bySort(s) {
    const sign = s.dir === 'asc' ? 1 : -1;
    return (a, b) => {
        const va = unopenedSortValue(a, s.key), vb = unopenedSortValue(b, s.key);
        if (va === null || vb === null)
            return va === vb ? byOldest(a, b) : va === null ? 1 : -1;
        const c = typeof va === 'number' && typeof vb === 'number' ? va - vb : COLLATOR.compare(String(va), String(vb));
        return sign * c || byOldest(a, b);
    };
}
export function unopenedPage(recs, q, now) {
    const kept = recs.filter((r) => keep(r, q, now)).sort(q.sort ? bySort(q.sort) : byOldest);
    const pages = Math.max(1, Math.ceil(kept.length / UNOPENED_PAGE));
    const page = Math.min(Math.max(1, Math.floor(q.page ?? 1)), pages);
    return { total: kept.length, page, pages, pageSize: UNOPENED_PAGE, records: kept.slice((page - 1) * UNOPENED_PAGE, page * UNOPENED_PAGE), facets: unopenedFacets(recs, q, now) };
}
/** the label of a light row's group (the row says which to-do it would be on the 14-day board) */
export const lightGroupLabel = (k) => TODO_GROUPS[k].label;
//# sourceMappingURL=unopened.js.map